import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { errorMessages } from '~/constants/errorMessages.ts';
import { openOrDownloadBlob } from '~/modules/covenants/utils.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesApplicationAmApprovalLetterClientSignedPath,
  getStringValue,
} from '~/modules/guarantees/utils.ts';
import { isGuaranteesApprovalLetterElectronicExecutionCheck } from '../../shared/approvalLetterExecution.ts';
import {
  selectGuaranteesAmTemplateDocumentError,
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesManagerSupervisorInfoStatus,
  selectGuaranteesSubmitApprovalLetterByAmManagerError,
  selectGuaranteesSubmitApprovalLetterByAmManagerStatus,
  selectHasGuaranteesAmAccess,
} from '~/store/guarantees/selectors.ts';
import {
  deleteGuaranteesClientDocument,
  downloadGuaranteesClientDocument,
  fetchGuaranteesAmTemplateDocument,
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationFiles,
  submitGuaranteesApprovalLetterByAmManager,
  uploadGuaranteesClientDocument,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  GuaranteesFileType,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import {
  notifyGuaranteesApplicationFilesChanged,
  useGuaranteesApplicationFilesSync,
} from '../shared/SidePanel/applicationFilesSync.ts';
import type { TClientUploadDocumentRow } from '../../client/step2/types.ts';
import {
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_FALLBACK_FILE_NAME,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_ACCESS_DENIED_TEXT,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_GET_STARTED_PATH,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH,
  MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT,
} from './constants.ts';
import {
  isManagerApprovalLetterManualSignStatus,
  mapManualSignedApprovalLetterFile,
} from './utils.ts';
import { MANAGER_APPROVAL_LETTER_TEXT } from '../step4/constants.ts';

export const useManagerApprovalLetterManualSignPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const managerSupervisorInfoStatus = useSelector(selectGuaranteesManagerSupervisorInfoStatus);
  const hasAmAccess = useSelector(selectHasGuaranteesAmAccess);
  const amTemplateDocumentError = useSelector(selectGuaranteesAmTemplateDocumentError);
  const submitApprovalLetterByAmManagerStatus = useSelector(
    selectGuaranteesSubmitApprovalLetterByAmManagerStatus
  );
  const submitApprovalLetterByAmManagerError = useSelector(
    selectGuaranteesSubmitApprovalLetterByAmManagerError
  );
  const [applicationFiles, setApplicationFiles] = useState<TGuaranteesApplicationFile[]>([]);
  const [isApplicationFilesLoading, setIsApplicationFilesLoading] = useState(false);
  const [hasApplicationFilesLoaded, setHasApplicationFilesLoaded] = useState(false);
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [actionMenuDocumentId, setActionMenuDocumentId] = useState<string | null>(null);
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const isAmApprovalLetterManualSignStatus =
    isManagerApprovalLetterManualSignStatus(normalizedStatus);
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isAmAccessLoading =
    managerSupervisorInfoStatus === GuaranteesFetchStatus.Idle ||
    managerSupervisorInfoStatus === GuaranteesFetchStatus.Loading;
  const isAmAccessDenied = !isAmAccessLoading && !hasAmAccess;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number'
      ? currentRouteApplication.version
      : undefined;
  const isSubmittingByAmManager =
    submitApprovalLetterByAmManagerStatus === GuaranteesFetchStatus.Loading;
  const manualSignedDocument = useMemo(
    () => mapManualSignedApprovalLetterFile(applicationFiles),
    [applicationFiles]
  );
  const isSubmitDisabled =
    currentApplicationVersion === undefined ||
    !effectiveApplicationId ||
    !manualSignedDocument ||
    isSubmittingByAmManager ||
    isApplicationLoading ||
    isApplicationFilesLoading;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const redirectPath = modalSuccess.text
    ? ''
    : !contragentId
      ? MANAGER_APPROVAL_LETTER_MANUAL_SIGN_GET_STARTED_PATH
      : currentRouteApplication && !isAmApprovalLetterManualSignStatus
        ? MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH
        : currentRouteApplication &&
            isGuaranteesApprovalLetterElectronicExecutionCheck(currentRouteApplication)
          ? buildGuaranteesApplicationAmApprovalLetterClientSignedPath(
              effectiveApplicationId,
              contragentId
            )
          : '';
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate(MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const handleCloseAmAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigate(MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH, { replace: true });
  }, [handleCloseAlertModal, navigate]);
  const navigateToTasks = useCallback(() => {
    navigate(MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TASKS_PATH, { replace: true });
  }, [navigate]);
  const handleSubmitByAmManager = useCallback(async () => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });

      return;
    }

    if (!manualSignedDocument) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.missingFileMessage });

      return;
    }

    try {
      await dispatch(
        submitGuaranteesApprovalLetterByAmManager({
          applicationId: effectiveApplicationId,
          version: currentApplicationVersion,
        })
      ).unwrap();
      setModalSuccess({
        text: MANAGER_APPROVAL_LETTER_TEXT.submitSuccessMessage,
        buttonValue: MANAGER_APPROVAL_LETTER_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    currentApplicationVersion,
    dispatch,
    effectiveApplicationId,
    manualSignedDocument,
    navigateToTasks,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
  ]);
  const refreshApplicationFiles = useCallback(async () => {
    if (!applicationId || applicationId === 'new') {
      setApplicationFiles([]);
      setHasApplicationFilesLoaded(true);

      return [];
    }

    setIsApplicationFilesLoading(true);

    try {
      const nextApplicationFiles = await dispatch(
        fetchGuaranteesApplicationFiles({ applicationId })
      ).unwrap();

      setApplicationFiles(nextApplicationFiles);
      setHasApplicationFilesLoaded(true);

      return nextApplicationFiles;
    } finally {
      setIsApplicationFilesLoading(false);
    }
  }, [applicationId, dispatch]);
  useGuaranteesApplicationFilesSync(effectiveApplicationId, () => {
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  });
  const handleAddDocument = async (file?: File | null) => {
    if (!file || manualSignedDocument) {
      return;
    }

    const documentId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setUploadingDocumentId(documentId);

    try {
      await dispatch(
        uploadGuaranteesClientDocument({
          applicationId: effectiveApplicationId,
          fileType: GuaranteesFileType.LETTER_OF_APPROVAL_MANUAL_SIGNED,
          multipartFile: file,
        })
      ).unwrap();

      await refreshApplicationFiles();
      notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setUploadingDocumentId(null);
    }
  };
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, documentId: string) => {
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuDocumentId(documentId);
  };
  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setActionMenuDocumentId(null);
  };
  const handleDownloadDocument = async (
    document: TClientUploadDocumentRow,
    forceDownload: boolean
  ) => {
    handleActionMenuClose();

    if (!document.fileId) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.missingFileMessage });

      return;
    }

    setDownloadingDocumentId(document.id);

    try {
      const downloadedDocument = await dispatch(
        downloadGuaranteesClientDocument({
          id: document.fileId,
          isAttached: !forceDownload,
        })
      ).unwrap();

      openOrDownloadBlob({
        blob: downloadedDocument.data,
        fileName: document.fileName || downloadedDocument.filename,
        fallbackFileName: MANAGER_APPROVAL_LETTER_MANUAL_SIGN_FALLBACK_FILE_NAME,
        contentType: downloadedDocument.contentType,
        forceDownload,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setDownloadingDocumentId(null);
    }
  };
  const handleDeleteUploadedDocument = async (document: TClientUploadDocumentRow) => {
    handleActionMenuClose();
    const applicationFileId = document.applicationFileId;

    if (!applicationFileId) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.missingFileMessage });

      return;
    }

    setDeletingDocumentId(document.id);

    try {
      await dispatch(deleteGuaranteesClientDocument({ applicationFileId })).unwrap();
      await refreshApplicationFiles();
      notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setDeletingDocumentId(null);
    }
  };

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
    void dispatch(fetchGuaranteesAmTemplateDocument({ applicationId }));
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  }, [applicationId, contragentId, dispatch, refreshApplicationFiles, showGuaranteesRequestError]);

  useFreshGuaranteesRequestError(
    applicationError || amTemplateDocumentError || submitApprovalLetterByAmManagerError,
    showGuaranteesRequestError
  );

  useEffect(() => {
    if (isAmAccessDenied) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_MANUAL_SIGN_ACCESS_DENIED_TEXT });
    }
  }, [isAmAccessDenied, setModalAlert]);

  return {
    actionMenuAnchorEl,
    actionMenuDocumentId,
    contragentId,
    deletingDocumentId,
    downloadingDocumentId,
    effectiveApplicationId,
    handleActionMenuClose,
    handleActionMenuOpen,
    handleAddDocument,
    handleCloseAlertModal,
    handleCloseAmAccessModal,
    handleCloseSuccessModal,
    handleDeleteUploadedDocument,
    handleDownloadDocument,
    handleSubmitByAmManager,
    hasApplicationFilesLoaded,
    headerProps,
    isAmAccessDenied,
    isAmAccessLoading,
    isApplicationFilesLoading,
    isApplicationLoading,
    isSubmitDisabled,
    isSubmittingByAmManager,
    manualSignedDocument,
    modalAlert,
    modalSuccess,
    redirectPath,
    setModalAlert,
    showGuaranteesRequestError,
    uploadingDocumentId,
  };
};
